import {
  Button,
  CardContent,
  Grid,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme,
} from '@material-ui/core/styles';
import { CancelToken } from 'axios';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import Card from '../../../../components/Card';
import { ChartData } from '../../../../models/dto/ExpectedRealizedDTO';
import {
  ChartDetailDTO,
  SystemAdherenceDTO,
} from '../../../../models/dto/SystemAdherenceDTO';
import { BaseEnum } from '../../../../models/enum/BaseEnum';
import SYSTEM_ADHERENCE_DATA_ENUM, {
  FUNCIONARIOS,
} from '../../../../models/enum/SystemAdherenceDataEnum';
import { getCancelTokenSource, isCancel } from '../../../../services/api';
import { findSystemAdherenceData } from '../../../../services/charts.service';
import {
  isAfter,
  startOf,
  subtract,
  toDate,
  toDefaultServerDateFormat,
} from '../../../../utils/date.util';
import { isEmpty, isNullOrEmpty } from '../../../../utils/validation.util';
import SystemAdherenceTableDialog from './SystemAdherenceTableDialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      height: '100%',
    },
    divider: {
      height: 40,
    },
    cardHeaderContainer: {
      padding: theme.spacing(2),
      paddingBottom: 0,
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: window.innerHeight * 0.23,
    },
    contentContainer: {
      height: '100%',
      paddingTop: 0,
    },
  }),
);

const SystemAdherenceCard: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [systemAdherenceData, setSystemAdherenceData] = useState<
    ChartDetailDTO[]
  >([]);
  const [chartSelected, setChartSelected] = useState<string>(
    FUNCIONARIOS.value,
  );

  const reduceSystemAdherenceData = (
    listToNormalize: ChartDetailDTO[],
  ): Map<string, any[]> => {
    const MONTH_STATUS_GROUP: { [key: string]: any } = {};
    const MONTH_GROUP: Map<string, any[]> = new Map();

    listToNormalize
      .sort((a, b) => {
        const dataInicioA = toDate(a.inicio);
        const dataInicioB = toDate(b.inicio);
        let result = 0;
        if (dataInicioA && dataInicioB) {
          result = isAfter(dataInicioA, dataInicioB) ? 1 : -1;
        }
        return result;
      })
      .map(item => {
        return {
          data: item.inicio?.slice(3),
          status: item.status,
          total: 1,
        };
      })
      .reduce((prev: any[], next) => {
        const key = `${next.data};${next.status}`;
        if (!MONTH_STATUS_GROUP[key]) {
          MONTH_STATUS_GROUP[key] = { ...next };
          prev.push(MONTH_STATUS_GROUP[key]);
        } else {
          MONTH_STATUS_GROUP[key].total += next.total;
        }
        return prev;
      }, [])
      .forEach(item => {
        const key = item.data;
        if (MONTH_GROUP.get(key)) {
          // eslint-disable-next-line no-unused-expressions
          MONTH_GROUP.get(key)?.push(item);
        } else {
          MONTH_GROUP.set(key, [{ ...item }]);
        }
      });
    return MONTH_GROUP;
  };

  const buildDataSet = useCallback(
    (chartdata: SystemAdherenceDTO) => {
      const labels: string[] = [];
      const deployed: number[] = [];
      const extended: number[] = [];
      const quitter: number[] = [];
      const underDeployment: number[] = [];
      const listToNormalize = isNullOrEmpty(chartdata.listFuncionarios)
        ? chartdata.listPredios
        : chartdata.listFuncionarios;
      setSystemAdherenceData(listToNormalize);

      if (!isNullOrEmpty(listToNormalize)) {
        const reducedData = reduceSystemAdherenceData(listToNormalize);
        // eslint-disable-next-line no-unused-expressions
        reducedData?.forEach((value: any[], key: any) => {
          labels.push(key);
          value.forEach(item => {
            switch (item.status) {
              case 'Implantado':
                deployed.push(item.total);
                break;
              case 'Desistente':
                quitter.push(item.total);
                break;
              case 'Prorrogado':
                extended.push(item.total);
                break;
              default:
                underDeployment.push(item.total);
                break;
            }
          });
        });
      }

      const builtChartData: ChartData = {
        labels,
        datasets: [
          {
            stack: 'stack1',
            label: 'Implantado',
            backgroundColor: theme.palette.success.main,
            hoverBackgroundColor: theme.palette.success.light,
            minBarLength: 10,
            data: deployed,
          },
          {
            stack: 'stack1',
            label: 'Desistente',
            backgroundColor: theme.palette.error.main,
            hoverBackgroundColor: theme.palette.error.light,
            minBarLength: 10,
            data: quitter,
          },
          {
            stack: 'stack1',
            label: 'Prorrogado',
            backgroundColor: theme.palette.warning.main,
            hoverBackgroundColor: theme.palette.warning.light,
            minBarLength: 10,
            data: extended,
          },
          {
            stack: 'stack1',
            label: 'Em Implantação',
            backgroundColor: theme.palette.grey[400],
            hoverBackgroundColor: theme.palette.grey[300],
            minBarLength: 10,
            data: underDeployment,
          },
        ],
      };

      setChartData(builtChartData);
    },
    [
      theme.palette.error.light,
      theme.palette.error.main,
      theme.palette.grey,
      theme.palette.success.light,
      theme.palette.success.main,
      theme.palette.warning.light,
      theme.palette.warning.main,
    ],
  );

  const findSystemAdherenceChartData = useCallback(
    async (type: string, cancelToken?: CancelToken) => {
      try {
        setLoading(true);
        const today = moment().toDate();
        const aYearAgo = toDefaultServerDateFormat(
          startOf(subtract(today, 1, 'year'), 'month'),
        );
        const currentDay = toDefaultServerDateFormat(today);
        if (!isEmpty(aYearAgo) && !isEmpty(currentDay)) {
          const { data } = await findSystemAdherenceData(
            type,
            aYearAgo,
            currentDay,
            cancelToken,
          );
          buildDataSet(data);
        }
      } catch (error) {
        if (!isCancel(error)) {
          console.log(error);
        }
      } finally {
        setLoading(false);
      }
    },
    [buildDataSet],
  );

  const handleChangeChartSelect = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      const typeSelected = event.target.value as string;
      if (typeSelected) {
        setChartSelected(typeSelected);
        findSystemAdherenceChartData(typeSelected);
      }
    },
    [findSystemAdherenceChartData],
  );

  useEffect(() => {
    const cancelTokenSource = getCancelTokenSource();
    findSystemAdherenceChartData(FUNCIONARIOS.value, cancelTokenSource.token);
  }, [findSystemAdherenceChartData]);

  const systemAdherenceChart = (
    <Bar
      data={chartData || {}}
      height={window.innerHeight * 0.165}
      options={{
        legend: {
          display: true,
          padding: 6,
          align: 'start',
          labels: {
            boxWidth: 10,
          },
          scales: {
            xAxes: [
              {
                stacked: true,
              },
            ],
            yAxes: [
              {
                stacked: true,
              },
            ],
          },
        },
        tooltips: {
          mode: 'label',
          callbacks: {
            label(tooltipItem: any, data: any) {
              return `${data.datasets[tooltipItem.datasetIndex].label}: ${
                tooltipItem.yLabel
              }`;
            },
          },
        },
      }}
    />
  );

  return (
    <Card className={classes.card}>
      <Grid
        container
        alignItems="center"
        justify="space-between"
        className={classes.cardHeaderContainer}
      >
        <Grid item>
          <Typography color="textSecondary" variant="h5">
            Adesão ao Ponto Digital
          </Typography>
        </Grid>
        <Grid item>
          <Button
            onClick={() => setOpenDialog(true)}
            variant="outlined"
            disabled={loading}
          >
            Abrir Listagem
          </Button>
        </Grid>
        <Grid container item>
          <Select
            disabled={loading}
            disableUnderline
            value={chartSelected}
            onChange={handleChangeChartSelect}
          >
            {loading ? (
              <MenuItem disabled value="" selected>
                Carregando...
              </MenuItem>
            ) : (
              SYSTEM_ADHERENCE_DATA_ENUM.map((item: BaseEnum) => (
                <MenuItem key={item.numberValue} value={item.value}>
                  {item.label}
                </MenuItem>
              ))
            )}
          </Select>
        </Grid>
      </Grid>

      <CardContent className={classes.contentContainer}>
        {!loading ? (
          <>{systemAdherenceChart}</>
        ) : (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        )}
      </CardContent>

      <SystemAdherenceTableDialog
        open={openDialog}
        handleClose={() => setOpenDialog(false)}
        handleConfirm={() => setOpenDialog(false)}
        showOneButton
        maxWidth="md"
        okButtonLabel="Fechar"
        showCloseButton
        loading={loading}
        systemAdherenceData={systemAdherenceData}
        type={chartSelected}
      />
    </Card>
  );
};

export default SystemAdherenceCard;
